import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getProductVehicleEquipmentList,
  addVehicleEquipmentToProduct,
  updateProductVehicleEquipment,
  deleteProductVehicleEquipment,
} from "../../../../../../apis/Product/ProductVehicleEquipment";
// import { getProdProductionMachineVehicleDropdown } from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import useBonsaiData from "../../../../../../hooks/useBonsaiData";

const ProdVehicleTable = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});

  // Use cached Bonsai data instead of individual API call
  const { data: dropdownUsedFor, isLoading: isBonsaiLoading } = useBonsaiData();

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getProductVehicleEquipmentList({
      productId,
      setObjects: setData,
    });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        const materialValue =
          row["production_vehicle"]?.value || row.production_vehicle;
        if (materialValue) {
          const selectedMaterial = dropdownUsedFor.find(
            (option) => option?.value === materialValue
          );
          if (selectedMaterial) {
            newUnitMaterials[row.id] = selectedMaterial.unit;
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, dropdownUsedFor]);

  const dynamicPlaceholders = useMemo(
    () => ({
      production_vehicle: {
        targetField: "qty",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedMaterial = dropdownData?.find(
            (option) => option?.value === selectedValue
          );
          if (selectedMaterial) {
            // For existing rows
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedMaterial.unit,
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedMaterial.unit, // Use a special key for new rows
              }));
            }
          }
        },
        dropdownDataKey: "production_vehicle",
      },
    }),
    []
  );
  const handleEdit = useCallback(
    (editedItem) => {
      const foundProduct = dropdownUsedFor?.find(
        (option) => option.name === editedItem.production_vehicle.value
      );
      const product_code = foundProduct
        ? foundProduct["code"]
        : editedItem.product_code;
      updateProductVehicleEquipment({
        productId,
        componentId: editedItem.id,
        ...editedItem,
        production_vehicle: editedItem.production_vehicle.value,
        product_code: product_code,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [productId, data, setData, dropdownUsedFor]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const foundProduct = dropdownUsedFor?.find(
        (option) => option.name === newItem.production_vehicle.value
      );

      const product_code = foundProduct ? foundProduct["code"] : "";
      addVehicleEquipmentToProduct({
        productId,
        ...newItem,
        production_vehicle: newItem.production_vehicle.value,
        product_code: product_code,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, fetchData, dropdownUsedFor]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteProductVehicleEquipment({
        productId,
        componentId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "vehicle",
        header: t("VEHICLE"),
        type: "text",
        disableSortBy: true,
        placeholder: t("TYPE_HERE"),
        width: "120px",
      },
      {
        accessor: "production_vehicle",
        header: `${t("ENERGY_TYPE")}*`,
        type: "dropdown",
        dropdownData: dropdownUsedFor,
        disableSortBy: true,
      },
      {
        accessor: "qty",
        header: t("QTY_FUNC_UNIT"),
        type: "number",
        tipHeader: "Tip",
        tipBody: `${t(
          "TIP_QTY_FUNC_COMP"
        )} Some selected items do not have units; "unit" will be shown as a placeholder.`,
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        unit: (rowId, rowData) => {
          const rowType =
            rowData?.production_vehicle?.value ||
            rowData?.["production_vehicle"]?.value;
          return rowType
            ? dropdownUsedFor.find((option) => option.value === rowType)?.unit
            : unitMaterials[rowId] || "unit";
        },
      },
      {
        accessor: "manuel",
        header: (props) => {
          const rowId = props.row?.original?.id;
          return `${t("Manual kgCO₂")} /${unitMaterials[rowId] || "unit"}`;
        },
        type: "number",
        tipHeader: "Tip",
        tipBody: `${t(
          "TIP_MANUEL_PROD"
        )} Some selected items do not have units; "unit" will be shown as a placeholder.`,
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        unit: (rowId, rowData) => {
          const rowType =
            rowData?.type?.value || rowData?.["production_vehicle"]?.value;
          return rowType
            ? "kgCO₂/" +
                dropdownUsedFor.find((option) => option.value === rowType)?.unit
            : unitMaterials[rowId]
            ? "kgCO₂/" + unitMaterials[rowId]
            : "kgCO₂/unit";
        },
        optional: true,
      },

      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "120px",
      },
    ],
    [dropdownUsedFor, t, unitMaterials]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      production_vehicle: dropdownUsedFor,
    }),
    [dropdownUsedFor]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default ProdVehicleTable;
